import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { subscriptionApi, plansApi } from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';

export default function Pricing() {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);

  const currentPlan = user?.subscription?.plan || 'free';

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const data = await plansApi.getAll();
      setPlans(data.plans || []);
    } catch (error) {
      console.error('Failed to load plans:', error);
    } finally {
      setLoadingPlans(false);
    }
  };

  const handleSubscribe = async (plan) => {
    if (!isAuthenticated) {
      navigate('/register', { state: { plan } });
      return;
    }

    setLoading(plan);
    try {
      const { url } = await subscriptionApi.createCheckout(plan);
      window.location.href = url;
    } catch (error) {
      console.error('Failed to create checkout:', error);
      alert('Failed to start checkout. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  // Parse features from JSON if needed
  const getFeatures = (plan) => {
    if (typeof plan.features === 'string') {
      try {
        return JSON.parse(plan.features);
      } catch {
        return [];
      }
    }
    return plan.features || [];
  };

  if (loadingPlans) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 py-16 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-gray-900">
            Simple, transparent pricing
          </h1>
          <p className="mt-4 text-xl text-gray-600 max-w-2xl mx-auto">
            Start free and upgrade when you're ready. No hidden fees, cancel anytime.
          </p>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan) => {
            const isCurrent = currentPlan === plan.id;
            const isDowngrade = 
              (currentPlan === 'premium' && plan.id !== 'premium') ||
              (currentPlan === 'basic' && plan.id === 'free');
            const features = getFeatures(plan);
            const price = parseFloat(plan.price) || 0;

            return (
              <div 
                key={plan.id}
                className={`relative bg-white rounded-3xl shadow-xl overflow-hidden transition-transform hover:-translate-y-1 ${
                  plan.is_featured ? 'ring-2 ring-primary-500' : ''
                }`}
              >
                {/* Popular Badge */}
                {plan.is_featured && (
                  <div className="absolute top-0 left-0 right-0 bg-primary-500 text-white text-center py-2 text-sm font-medium">
                    Most Popular
                  </div>
                )}

                <div className={`p-8 ${plan.is_featured ? 'pt-14' : ''}`}>
                  {/* Plan Name */}
                  <h2 className="font-display text-2xl font-bold text-gray-900">
                    {plan.name}
                  </h2>
                  <p className="text-gray-500 mt-1">
                    {price === 0 ? 'No credit card required' : 'Cancel anytime'}
                  </p>

                  {/* Price */}
                  <div className="mt-6">
                    <span className="text-5xl font-bold text-gray-900">
                      ${price.toFixed(0)}
                    </span>
                    <span className="text-gray-500">/{plan.billing_period}</span>
                  </div>

                  {/* Features */}
                  <ul className="mt-8 space-y-4">
                    {features.map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <div className="mt-8">
                    {isCurrent ? (
                      <button 
                        disabled
                        className="w-full py-4 px-6 rounded-full font-semibold bg-gray-100 text-gray-500 cursor-default"
                      >
                        Current Plan
                      </button>
                    ) : isDowngrade ? (
                      <button 
                        disabled
                        className="w-full py-4 px-6 rounded-full font-semibold bg-gray-100 text-gray-400 cursor-not-allowed"
                      >
                        Contact to Downgrade
                      </button>
                    ) : plan.id === 'free' ? (
                      <Link 
                        to="/register"
                        className={`block w-full py-4 px-6 rounded-full font-semibold text-center ${
                          plan.is_featured 
                            ? 'bg-primary-500 text-white hover:bg-primary-600' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        } transition-colors`}
                      >
                        {plan.button_text || 'Get Started Free'}
                      </Link>
                    ) : (
                      <button 
                        onClick={() => handleSubscribe(plan.id)}
                        disabled={loading === plan.id}
                        className={`w-full py-4 px-6 rounded-full font-semibold transition-colors ${
                          plan.is_featured 
                            ? 'bg-primary-500 text-white hover:bg-primary-600' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        } disabled:opacity-50`}
                      >
                        {loading === plan.id ? (
                          <span className="flex items-center justify-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Loading...
                          </span>
                        ) : (
                          plan.button_text || 'Subscribe'
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* FAQ Section */}
        <div className="mt-20 max-w-3xl mx-auto">
          <h2 className="font-display text-3xl font-bold text-gray-900 text-center mb-12">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            {[
              {
                q: 'Can I try before I buy?',
                a: 'Absolutely! Our Free plan gives you access to the first 20 levels of Pip\'s Kitchen Adventure with no credit card required. Play as long as you want before deciding to upgrade.'
              },
              {
                q: 'Can I cancel anytime?',
                a: 'Yes, you can cancel your subscription at any time from your account dashboard. You\'ll continue to have access until the end of your billing period.'
              },
              {
                q: 'Will my progress be saved if I upgrade later?',
                a: 'Yes! All your progress is saved to your account. When you upgrade, you\'ll pick up exactly where you left off, plus unlock additional levels.'
              },
              {
                q: 'Do you offer refunds?',
                a: 'We offer a 7-day money-back guarantee on all paid plans. If you\'re not satisfied, contact us within 7 days of your purchase for a full refund.'
              },
              {
                q: 'Can multiple children use one account?',
                a: 'Yes! The Basic plan supports up to 3 child profiles, and Premium offers unlimited profiles. Each child gets their own progress tracking.'
              },
            ].map((faq, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="font-display font-bold text-gray-900 text-lg">
                  {faq.q}
                </h3>
                <p className="mt-2 text-gray-600">
                  {faq.a}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Contact */}
        <div className="mt-16 text-center">
          <p className="text-gray-600">
            Have questions? We're here to help.{' '}
            <a href="mailto:support@speechformation.com" className="text-primary-600 font-medium hover:text-primary-700">
              Contact us
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
